import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, FileText, Folder, FolderUp, Loader2, Upload, XCircle } from 'lucide-react';
import { useAuditYear } from '../../../contexts/AuditYearContext.tsx';
import { useInvalidateLibrary, useLibraryLimits, toastLibraryError } from '../../../hooks/evidence-library/useEvidenceLibrary.ts';
import type { BatchScope, EvidenceRole } from '../../../request/evidence-library/evidenceLibraryRequests.ts';
import {
  buildTree,
  entriesFromDrop,
  entriesFromInput,
  formatBytes,
  summarizeSelection,
  toPickedFiles,
  type FolderNode,
  type PickedFile,
} from '../folderFiles.ts';
import { countOutcomes, runLibraryUpload, type FileOutcome, type UploadRunResult } from '../uploadRunner.ts';
import { ROLE_LABELS } from '../labels.ts';
import { LibraryDialogShell, fieldClass, primaryBtn, secondaryBtn } from './LibraryDialogShell.tsx';

export type FolderUploadContext = {
  scope: BatchScope;
  soxControlId?: string;
  testRunId?: string;
  /** Role given to every file (control/run uploads). Library uploads leave files unclassified. */
  fileCategory?: EvidenceRole;
  periodStart?: string;
  periodEnd?: string;
};

type Phase = 'pick' | 'uploading' | 'done';

function filesUnder(node: FolderNode): PickedFile[] {
  return [...node.files, ...node.folders.flatMap(filesUnder)];
}

function FolderRow({
  node,
  depth,
  excluded,
  setExcluded,
  disabled,
}: {
  node: FolderNode;
  depth: number;
  excluded: Set<string>;
  setExcluded: (fn: (prev: Set<string>) => Set<string>) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(depth < 1);
  const all = useMemo(() => filesUnder(node).filter((f) => !f.skipReason), [node]);
  const included = all.filter((f) => !excluded.has(f.id)).length;
  const checked = all.length > 0 && included === all.length;
  const partial = included > 0 && included < all.length;
  const toggle = () =>
    setExcluded((prev) => {
      const next = new Set(prev);
      // A fully ticked folder unticks; a partly or un-ticked one ticks everything in it.
      if (checked) all.forEach((f) => next.add(f.id));
      else all.forEach((f) => next.delete(f.id));
      return next;
    });
  return (
    <li>
      <div className="flex items-center gap-2 py-1" style={{ paddingLeft: depth * 16 }}>
        <button type="button" onClick={() => setOpen((o) => !o)} className="rounded p-0.5 text-gray-500 hover:bg-gray-100" aria-label={open ? 'Collapse folder' : 'Expand folder'}>
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <input
          type="checkbox"
          checked={checked}
          ref={(el) => { if (el) el.indeterminate = partial; }}
          onChange={toggle}
          disabled={disabled || all.length === 0}
          aria-label={`Include folder ${node.name}`}
        />
        <Folder className="h-4 w-4 shrink-0 text-amber-500" />
        <span className="truncate text-sm font-medium text-gray-800">{node.name}</span>
        <span className="ml-auto shrink-0 text-xs text-gray-400">{node.fileCount} files · {formatBytes(node.bytes)}</span>
      </div>
      {open ? (
        <ul>
          {node.folders.map((child) => (
            <FolderRow key={child.path} node={child} depth={depth + 1} excluded={excluded} setExcluded={setExcluded} disabled={disabled} />
          ))}
          {node.files.map((f) => (
            <FileRow key={f.id} file={f} depth={depth + 1} excluded={excluded} setExcluded={setExcluded} disabled={disabled} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function FileRow({
  file,
  depth,
  excluded,
  setExcluded,
  disabled,
  outcome,
}: {
  file: PickedFile;
  depth: number;
  excluded: Set<string>;
  setExcluded: (fn: (prev: Set<string>) => Set<string>) => void;
  disabled: boolean;
  outcome?: FileOutcome;
}) {
  const name = file.path.split('/').pop();
  return (
    <li className="flex items-center gap-2 py-0.5" style={{ paddingLeft: depth * 16 + 20 }}>
      <input
        type="checkbox"
        checked={!file.skipReason && !excluded.has(file.id)}
        disabled={disabled || Boolean(file.skipReason)}
        onChange={() =>
          setExcluded((prev) => {
            const next = new Set(prev);
            if (next.has(file.id)) next.delete(file.id);
            else next.add(file.id);
            return next;
          })
        }
        aria-label={`Include ${name}`}
      />
      <FileText className="h-3.5 w-3.5 shrink-0 text-gray-400" />
      <span className={`truncate text-sm ${file.skipReason ? 'text-gray-400 line-through' : 'text-gray-700'}`} title={file.path}>{name}</span>
      {file.skipReason ? <span className="ml-auto shrink-0 text-xs text-amber-600">{file.skipReason}</span> : null}
      {!file.skipReason && outcome ? <span className="ml-auto shrink-0 text-xs text-gray-500">{outcome.state}</span> : null}
      {!file.skipReason && !outcome ? <span className="ml-auto shrink-0 text-xs text-gray-400">{formatBytes(file.file.size)}</span> : null}
    </li>
  );
}

const OUTCOME_STYLE: Record<string, string> = {
  uploaded: 'text-emerald-700',
  duplicate: 'text-sky-700',
  skipped: 'text-amber-700',
  failed: 'text-red-700',
};

export function FolderUploadDialog({
  title = 'Upload to evidence library',
  context,
  onClose,
  onUploaded,
}: {
  title?: string;
  context: FolderUploadContext;
  onClose: () => void;
  /** Called after each upload run with every file's outcome (upload ids, evidence ids). */
  onUploaded?: (result: UploadRunResult, files: PickedFile[]) => void;
}) {
  const { data: limits, isLoading: limitsLoading, error: limitsError } = useLibraryLimits();
  const { auditYears, selectedAuditYearId } = useAuditYear();
  const invalidate = useInvalidateLibrary();

  const [files, setFiles] = useState<PickedFile[]>([]);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [auditYearId, setAuditYearId] = useState<string>('');
  const [label, setLabel] = useState('');
  const [phase, setPhase] = useState<Phase>('pick');
  const [outcomes, setOutcomes] = useState<Record<string, FileOutcome>>({});
  const [dragging, setDragging] = useState(false);
  const folderInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const addEntries = useCallback(
    (entries: { file: File; path: string }[]) => {
      if (!limits || !entries.length) return;
      setFiles((prev) => toPickedFiles([...prev.map((p) => ({ file: p.file, path: p.path })), ...entries], limits));
    },
    [limits]
  );

  const tree = useMemo(() => buildTree(files), [files]);
  const skipped = files.filter((f) => f.skipReason);
  const summary = limits ? summarizeSelection(files, excluded, limits) : null;
  const yearChoosable = context.scope !== 'run';
  const writableYears = auditYears.filter((y) => !y.is_locked);
  // Default to the year selected in the app when it is open, otherwise the first open year; never a locked one.
  useEffect(() => {
    if (auditYearId && writableYears.some((y) => y.id === auditYearId)) return;
    const preferred = writableYears.find((y) => y.id === selectedAuditYearId) ?? writableYears[0];
    if (preferred && preferred.id !== auditYearId) setAuditYearId(preferred.id);
  }, [auditYears, selectedAuditYearId]); // eslint-disable-line react-hooks/exhaustive-deps
  const counts = countOutcomes(outcomes);
  const uploading = phase === 'uploading';

  const start = async (subset?: PickedFile[]) => {
    if (!limits) return;
    const toSend = subset ?? files.filter((f) => !f.skipReason && !excluded.has(f.id));
    if (!toSend.length) return;
    setPhase('uploading');
    try {
      const result = await runLibraryUpload({
        files: toSend,
        limits,
        onProgress: (o) => setOutcomes((prev) => ({ ...prev, ...o })),
        batch: {
          scope: context.scope,
          audit_year_id: yearChoosable ? auditYearId || undefined : undefined,
          sox_control_id: context.soxControlId,
          test_run_id: context.testRunId,
          file_category: context.fileCategory,
          testing_period_start: context.periodStart,
          testing_period_end: context.periodEnd,
          label: label.trim() || undefined,
        },
      });
      invalidate();
      onUploaded?.(result, toSend);
    } catch (e) {
      // The batch could not start (limits, locked year): nothing was uploaded.
      toastLibraryError(e, 'Upload could not start');
      setPhase('pick');
      return;
    }
    setPhase('done');
  };

  const failedFiles = files.filter((f) => outcomes[f.id]?.state === 'failed');

  const body = () => {
    if (limitsLoading) return <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading upload limits…</p>;
    if (limitsError || !limits) return <p className="text-sm text-red-600">Could not load the upload limits. Close and try again.</p>;

    if (phase !== 'pick') {
      const pct = counts.total ? Math.round((counts.done / counts.total) * 100) : 0;
      const notable = files.filter((f) => {
        const o = outcomes[f.id];
        return o && (o.state === 'failed' || o.state === 'skipped' || (o.state === 'duplicate' && !o.linked));
      });
      return (
        <div className="space-y-4">
          <div>
            <div className="mb-1 flex justify-between text-sm text-gray-700">
              <span>{uploading ? 'Uploading…' : 'Upload finished'}</span>
              <span>{counts.done} / {counts.total}</span>
            </div>
            <div className="h-2 overflow-hidden rounded bg-gray-100">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <div className={`grid grid-cols-2 gap-2 text-sm ${context.scope === 'library' ? 'sm:grid-cols-4' : 'sm:grid-cols-5'}`}>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800"><b>{counts.uploaded}</b> uploaded</div>
            {context.scope !== 'library' ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800" title="Same content was already in the library: that file was added here instead of storing a second copy.">
                <b>{counts.linked}</b> added from library
              </div>
            ) : null}
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sky-800"><b>{counts.duplicate}</b> already in library</div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800"><b>{counts.skipped + skipped.length}</b> skipped</div>
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800"><b>{counts.failed}</b> failed</div>
          </div>
          {!uploading && notable.length ? (
            <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 text-sm">
              {notable.map((f) => {
                const o = outcomes[f.id];
                return (
                  <li key={f.id} className="flex items-center gap-2 px-3 py-1.5">
                    <span className="truncate text-gray-700" title={f.path}>{f.path}</span>
                    <span className={`ml-auto shrink-0 text-xs ${OUTCOME_STYLE[o.state] ?? ''}`}>{o.reason || o.state}</span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={async (e) => {
            e.preventDefault();
            setDragging(false);
            try {
              addEntries(await entriesFromDrop(e.dataTransfer));
            } catch {
              toastLibraryError(null, 'Could not read the dropped folder');
            }
          }}
          className={`rounded-xl border-2 border-dashed px-4 py-6 text-center ${dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`}
        >
          <FolderUp className="mx-auto h-8 w-8 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">Drop a folder or files here, or</p>
          <div className="mt-3 flex justify-center gap-2">
            <button type="button" className={secondaryBtn} onClick={() => folderInput.current?.click()}>Choose folder</button>
            <button type="button" className={secondaryBtn} onClick={() => fileInput.current?.click()}>Choose files</button>
          </div>
          <input
            ref={folderInput}
            type="file"
            className="hidden"
            // React does not type the folder picker attributes.
            {...({ webkitdirectory: '', directory: '' } as any)}
            multiple
            onChange={(e) => { addEntries(entriesFromInput(e.target.files)); e.target.value = ''; }}
          />
          <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => { addEntries(entriesFromInput(e.target.files)); e.target.value = ''; }} />
          <p className="mt-3 text-xs text-gray-500">
            Up to {limits.max_files_per_upload} files and {formatBytes(limits.max_bytes_per_upload)} per upload, {formatBytes(limits.max_file_bytes)} per file.
            {context.fileCategory
              ? ` Files are added as ${ROLE_LABELS[context.fileCategory]}.`
              : context.scope === 'library'
                ? ' Files stay unclassified until you use them.'
                : ' Files are added to this control as Evidence; you can change a file\'s role later.'}
          </p>
        </div>

        {yearChoosable || context.scope === 'library' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {yearChoosable ? (
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Audit year</span>
                <select className={fieldClass} value={auditYearId} onChange={(e) => setAuditYearId(e.target.value)}>
                  {!auditYearId ? <option value="">Select a year</option> : null}
                  {writableYears.map((y) => (
                    <option key={y.id} value={y.id}>{y.name}</option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Label (optional)</span>
              <input className={fieldClass} value={label} maxLength={255} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Q3 PBC from Finance" />
            </label>
          </div>
        ) : null}

        {files.length ? (
          <>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="font-medium text-gray-800">{summary?.count ?? 0} of {files.length} files selected · {formatBytes(summary?.bytes ?? 0)}</span>
              {skipped.length ? <span className="text-amber-700">{skipped.length} will be skipped</span> : null}
              <button type="button" className="ml-auto text-xs text-gray-500 underline hover:text-gray-700" onClick={() => { setFiles([]); setExcluded(new Set()); }}>Clear</button>
            </div>
            {summary?.overFiles || summary?.overBytes ? (
              <p className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {summary.overFiles
                  ? `One upload can hold at most ${limits.max_files_per_upload} files. Untick folders you don't need, or split the upload.`
                  : `One upload can hold at most ${formatBytes(limits.max_bytes_per_upload)}. Untick folders you don't need, or split the upload.`}
              </p>
            ) : null}
            <ul className="max-h-80 overflow-y-auto rounded-lg border border-gray-200 px-2 py-1">
              {tree.folders.map((node) => (
                <FolderRow key={node.path} node={node} depth={0} excluded={excluded} setExcluded={setExcluded} disabled={false} />
              ))}
              {tree.files.map((f) => (
                <FileRow key={f.id} file={f} depth={-1} excluded={excluded} setExcluded={setExcluded} disabled={false} />
              ))}
            </ul>
          </>
        ) : null}
      </div>
    );
  };

  const footer = () => {
    if (phase === 'uploading') {
      return <span className="flex items-center gap-2 text-sm text-gray-600"><Loader2 className="h-4 w-4 animate-spin" /> Keep this window open until the upload finishes.</span>;
    }
    if (phase === 'done') {
      return (
        <>
          {failedFiles.length ? (
            <button type="button" className={secondaryBtn} onClick={() => start(failedFiles)}>
              Retry {failedFiles.length} failed
            </button>
          ) : null}
          <button type="button" className={primaryBtn} onClick={onClose}>
            {counts.failed ? <XCircle className="mr-1 inline h-4 w-4" /> : <CheckCircle2 className="mr-1 inline h-4 w-4" />}
            Done
          </button>
        </>
      );
    }
    const blocked = !summary || summary.count === 0 || summary.overFiles || summary.overBytes || (yearChoosable && !auditYearId);
    return (
      <>
        <button type="button" className={secondaryBtn} onClick={onClose}>Cancel</button>
        <button type="button" className={primaryBtn} disabled={blocked} onClick={() => start()}>
          <Upload className="mr-1 inline h-4 w-4" />
          Upload {summary?.count ? `${summary.count} files` : ''}
        </button>
      </>
    );
  };

  return (
    <LibraryDialogShell title={title} subtitle={files.length && phase === 'pick' ? 'Check the files below before uploading.' : undefined} onClose={onClose} closeDisabled={uploading} footer={footer()}>
      {body()}
    </LibraryDialogShell>
  );
}
